import { Box, Stack, Typography, IconButton } from "@mui/material";
import FacebookIcon from "@mui/icons-material/Facebook";
import { Phone, Mail } from "lucide-react";
import {
  Instagram,
  Language,
  LinkedIn,
  Twitter,
  WhatsApp,
  YouTube,
} from "@mui/icons-material";
import { useGetFooterConfigQuery } from "../../../../service/footerConfigApi";

const FOOTER_SOURCE = "MMS";

const SOCIAL_ICON_MAP: Record<string, { Icon: React.ElementType; color: string }> = {
  Facebook: { Icon: FacebookIcon, color: "#1877F2" },
  LinkedIn: { Icon: LinkedIn, color: "#0A66C2" },
  YouTube: { Icon: YouTube, color: "#FF0000" },
  WhatsApp: { Icon: WhatsApp, color: "#25D366" },
  Instagram: { Icon: Instagram, color: "#E4405F" },
  Twitter: { Icon: Twitter, color: "#1DA1F2" },
};

const fallbackSocialLinks = [
  {
    Icon: FacebookIcon,
    color: "#1877F2",
    href: "https://www.facebook.com/share/18wBS1THMb/",
  },
  {
    Icon: LinkedIn,
    color: "#0A66C2",
    href: "https://www.linkedin.com/company/muzukuru/",
  },
  {
    Icon: YouTube,
    color: "#FF0000",
    href: "https://www.youtube.com/@Muzukuru",
  },
  {
    Icon: WhatsApp,
    color: "#25D366",
    href: "https://wa.me/263776402402",
  },
];

const fallbackPhone = "+263 776 402 402";
const fallbackEmail = "cas@area46.co.zw";

interface TopBannerProps {
  backgroundColor: string;
}

const TopBanner = ({ backgroundColor }: TopBannerProps) => {
  const { data: footerConfig } = useGetFooterConfigQuery(FOOTER_SOURCE);

  const socialLinks =
    footerConfig?.socials && footerConfig.socials.length > 0
      ? footerConfig.socials.map((s) => {
          const match = SOCIAL_ICON_MAP[s.icon] ?? SOCIAL_ICON_MAP[s.platform];
          return {
            Icon: match?.Icon ?? Language,
            color: match?.color ?? "#666",
            href: s.url,
          };
        })
      : fallbackSocialLinks;

  const phone =
    footerConfig?.contact_details?.find((c) => c.type === "phone")?.value ||
    fallbackPhone;

  const email =
    footerConfig?.contact_details?.find((c) => c.type === "email")?.value ||
    fallbackEmail;

  return (
    <Box
      sx={{
        display: {
          xs: "none",
          lg: "block",
        },

        bgcolor: backgroundColor,
        color: "#fff",

        borderBottom: "1px solid rgba(255,255,255,.1)",
      }}
    >
      <Box
        sx={{
          maxWidth: "1400px",
          mx: "auto",
          height: 44,

          px: {
            xs: 2,
            lg: 4,
          },
          py: {
            xs: 2,
            lg: 4,
          },

          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* Left */}
        <Stack direction="row" spacing={4} alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <Phone size={14} />

            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              {phone}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Mail size={14} />

            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              {email}
            </Typography>
          </Stack>
        </Stack>

        {/* Right */}
        <Stack direction="row" spacing={3} alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            {socialLinks.map(({ Icon, color, href }, index) => (
              <IconButton
                key={index}
                component="a"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: "#fff",
                  color,

                  "&:hover": {
                    bgcolor: "#fff",
                    transform: "scale(1.05)",
                  },

                  transition: "all .2s",
                }}
              >
                <Icon fontSize="small" />
              </IconButton>
            ))}
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default TopBanner;